/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Platform } from 'react-native';
import axiosInstance from './axios';
import Order, { Payment } from 'types/models/Order';
import ShoppingCart from 'types/models/ShoppingCart';
import OrderDetail from 'types/models/OrderDetail';
import { parseEvent } from './eventServices';
import { parseCloverPointPayment, parseFDPayment, parseFiservQrPayment } from './paymentsServices';
import ConsumptionReportPayloadData from 'types/models/ConsumptionReportPayloadData';
import { parseProduct } from './productService';
import { parseUser } from './authService';
import OrderImage from 'types/models/OrderImage';

export const getOrders = async (commerceId: string, venueId: string, params: any): Promise<Order[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `orders/${commerceId}/${venueId}/orders/get/active`,
      params,
    });
    return parseOrders(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getPaginatedOrders = async (
  commerceId: string,
  venueId: string,
  params: any,
  page: number,
  orderBy: string | null = null,
): Promise<{ currentPage: number; data: Order[]; perPage: number; total: number; from: number; to: number }> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `orders/${commerceId}/${venueId}/orders/get/active`,
      params: {
        ...params,
        page,
        orderById: orderBy,
      },
    });
    return {
      currentPage: paginationData.current_page,
      data: parseOrders(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
      from: paginationData.from,
      to: paginationData.to,
    };
  } catch (error) {
    console.error(error);
    return {
      currentPage: 1,
      data: [],
      perPage: 15,
      total: 0,
      from: 0,
      to: 0,
    };
  }
};

export const getInProcessOrders = async (
  commerceId: string,
  venueId: string,
  billingRange: number
): Promise<{
  pending: number;
  inProcess: number;
  toDelivery: number;
  totalAmount: number;
}> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `orders/${commerceId}/${venueId}/orders/get/active`,
      params: {
        isForGetByStatuses: true,
        billingRange
      },
    });
    return {
      pending: data.pending,
      inProcess: data.inProcess,
      toDelivery: data.toDelivery,
      totalAmount: data.totalAmount
    };
  } catch (error) {
    console.error(error);
    return {
      pending: 0,
      inProcess: 0,
      toDelivery: 0,
    };
  }
};

export const validateRefundValidationCode = async (body: any): Promise<any> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `users/validate-refund-validation-code`,
      data: body,
    });
    return data;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const refundOrder = async (body: any): Promise<any> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `orders/refund`,
      data: body,
    });
    return data;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const sendInvoice = async (body: any): Promise<any> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `orders/send-invoice`,
      data: body,
    });
    return data;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const getOrder = async (orderId: number): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/orders/${orderId}`,
    });
    return parseOrder(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const deliverOrderByQr = async ({
  code,
  commerceId,
}: {
  code: number;
  commerceId: string;
}): Promise<{
  status: string;
  orderId: number;
  validated?: number;
  total?: number;
  productName?: string;
  order?: Order;
  scannedProductId?: string;
} | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `/orders/${code}/deliver-by-qr`,
      data: { commerceId },
    });
    return { ...data, order: data.order ? parseOrder(data.order) : null };
  } catch (error) {
    console.log('error(deliverOrderByQr)', error);
    return null;
  }
};

export const updateOrderStatus = async (order: Order): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `/orders/${order.id}`,
      data: {
        ...order,
        orderStatusId: order.status,
      },
    });
    return parseOrder(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateCashPayment = async (order: Order): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `/payments/cash`,
      data: {
        orderId: order.id,
      },
    });
    return parseOrder(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeFoodTruckOrder = async (
  order: ShoppingCart,
  venueId: string,
  localTimeZone: string,
): Promise<Order | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'POST',
      url: 'truck-orders',
      data: {
        ...order,
        venueId,
        localTimeZone,
      },
    });
    return parseOrder(data);
  } catch (error: any) {
    console.log(error);
    return null;
  }
};

export const getTotalBillingAndCommercesAmount = async ({
  commerceIds,
  billingRange,
}: {
  commerceIds: string[];
  billingRange: number;
}): Promise<{ balance: number; commerces: Record<string, number> }> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `billing/total`,
      data: {
        commerceIds,
        billingRange,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getReportingOrdersSummary = async (formData: ConsumptionReportPayloadData): Promise<any> => {
  try {
    const response = await axiosInstance({
      method: 'post',
      url: `reporting/summary`,
      data: {
        ...formData,
      },
    });
    return response.data; // Retorna { total_transactions, total_amount }
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getReportingOrdersTotal = async (formData: any) => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `reporting/orders/total`,
      data: formData,
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const getReportingOrders = async (formData: ConsumptionReportPayloadData, page?: number) => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'post',
      url: `reporting/orders?page=${page ?? 1}`,
      data: {
        ...formData,
        download: false,
      },
    });
    return {
      currentPage: paginationData.current_page,
      data: parseOrders(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
      // Para cashless
      from: paginationData.from,
      to: paginationData.to,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAllReportingOrders = async (
  formData: ConsumptionReportPayloadData,
  lang: string,
): Promise<ArrayBuffer | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `reporting/orders`,
      data: {
        ...formData,
        download: true,
        lang,
      },
      responseType: Platform.OS === 'web' ? 'blob' : 'arraybuffer',
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const parseOrders = (orders: any): Order[] => {
  return orders.map((order: any) => {
    return parseOrder(order);
  });
};

const parseDetailsOrder = (details: []): OrderDetail[] => {
  return details.map((detail) => parseDetails(detail));
};

const parseDetails = (detail: any): OrderDetail => ({
  id: detail.id,
  product: parseProduct(detail.product),
  price: detail.price,
  quantity: detail.quantity,
  totalAmount: detail.total_amount,
  comments: detail.comments,
  tomorrowQuantityBenefit: detail.tomorrow_benefit_quantity ?? undefined,
  tomorrowBenefitDiscountPercentage: detail.tomorrow_benefit_discount_percentage ?? undefined,
  tomorrowBuyerMatricula: detail.tomorrow_buyer_matricula,
});

export const parseOrder = (order: any): Order => {
  return {
    id: order.id,
    totalAmount: order.total_amount,
    verificationCode: order.verification_code,
    status: order.order_status_id,
    phone: order.phone,
    isCash: order.is_cash === 1,
    isCourtesy: order.is_courtesy === 1,
    isDiscount: order.is_discount === 1,
    commerceId: order.commerce_id,
    paymentId: order.payment_id,
    createdAt: order.created_at,
    updatedAt: order.updated_at,
    localDateTime: order.local_datetime,
    commerce: order.commerce,
    orderDetails: order.order_detail ? parseDetailsOrder(order.order_detail) : [],
    buyer: order.buyer,
    refund: order.refund ? { id: order.refund.id } : null,
    refundOfOrder: order.refund_of_order,
    event: order.event ? parseEvent(order.event) : undefined,
    identification: order.identification,
    guestEmail: order.guest_email,
    delivery: order.delivery,
    deliveryTimeRange: order.delivery_time_range,
    deliveryAddress: order.delivery_address,
    firstDataPayment: order?.payment?.first_data_payment ? parseFDPayment(order.payment.first_data_payment) : null,
    cloverDataPayment: order?.payment?.clover_point_payment
      ? parseCloverPointPayment(order?.payment?.clover_point_payment)
      : null,
    fiservQrDataPayment: order?.payment?.fiserv_qr_subsidiary_sales_stand_payment
      ? parseFiservQrPayment(order?.payment?.fiserv_qr_subsidiary_sales_stand_payment)
      : null,
    user: order.user ? parseUser(order.user) : undefined,
    discountPercentage: order.discount_percentage,
    payment: order.payment ? parsePayment(order.payment) : undefined,
    images: order.images ? parseOrderImages(order.images) : undefined,
    assignedPickupVerificationMethod: order.assigned_pickup_verification_method,
    isPreOrder: order.is_pre_order,
  };
};

const parseOrderImages = (images: any[]): OrderImage[] => {
  return images.map((image) => ({
    orderId: image.pivot.order_id,
    orderImageTypeId: image.pivot.order_image_type_id,
    path: image.path,
    orderDetailId: image.pivot.order_detail_id,
  }));
};

const parsePayment = (payment: any): Payment => ({
  id: payment.id,
  createdAt: payment.created_at,
  isCash: payment.is_cash == 1,
  isBalance: payment.is_balance == 1,
  isCourtesy: payment.is_courtesy === 1,
  isSplitPayment: payment.is_split_payment === 1,
  isForCheckingAccount: payment.is_for_checking_account === 1,
  cloverPointPaymentId: payment.clover_point_payment_id,
  partialAmount: payment.partial_amount,
  fiservQrPayment: payment.fiserv_qr_subsidiary_sales_stand_payment
    ? parseFiservQrPayment(payment.fiserv_qr_subsidiary_sales_stand_payment)
    : undefined,
  cloverPointPayment: payment.clover_point_payment ? parseCloverPointPayment(payment.clover_point_payment) : undefined,
  subPayments: payment.sub_payments ? parsePayments(payment.sub_payments) : [],
});

const parsePayments = (payments: any[]) => payments.map((item) => parsePayment(item));
